import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getProductsYMMD from "@store/Store/Components/RsswYMMD/Handlers/getProductsYMMD";
import { useProductListYmmdPage } from "@store/Store/Components/RsswYMMD/Hooks/useYMMD";
import { getState } from "@store/Store/Context/ContextSelectors";
import { getPolicy } from "@store/Store/Data/getPolicy";
import getCompatibleVehiclesList from "@store/Store/Pages/ProductDetails/Handlers/GetCompatibleVehiclesList";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import React, { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { css } from "styled-components";

const enum fields {
    hideLogo = "hideLogo",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.hideLogo]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        session: state.context.session,
    };
};

type Props = OwnProps & HasProduct & HasHistory & ReturnType<typeof mapStateToProps>;

export interface CompatibleVehicleStyles {
    wrapper?: InjectableCss;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    link?: LinkPresentationProps;
}

interface MakesResultModel {
    name: string;
    modelYearResults: Array<object>;
}

export const compatibleVehicleStyles: CompatibleVehicleStyles = {
    wrapper: {
        css: css`
            border-bottom: 1px solid ${COLORS.border.base};
        `,
    },
    accordionSection: {
        headerProps: {
            css: css`
                button {
                    padding: 12px 0 10px !important;
                }
            `,
        },
        titleTypographyProps: {
            transform: "uppercase",
            css: css`
                margin: 0 10px;
            `,
        },
        panelProps: {
            css: css`
                padding: 0 10px 20px;
                border: 0;

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: ["1", "2", "3", "4", "5"], properties: ["column-count"] }],
                        mediaOptions.min,
                    )}
            `,
        },
        mergeCss: true,
    },
    link: {
        color: COLORS.text.base,
        hoverStyle: {
            color: COLORS.link.base,
            textDecoration: "underline",
        },
        css: css`
            display: flex;

            > span {
                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [
                            {
                                values: [`${TYPOGRAPHY.size.base}`, null, `${TYPOGRAPHY.size.smaller}`, null, null],
                                properties: ["font-size"],
                            },
                            { values: ["5px", null, "0", null, null], properties: ["margin-bottom"] },
                        ],
                        mediaOptions.min,
                    )}
            }
        `,
    },
};

const styles = compatibleVehicleStyles;

const ProductDetailsCompatibleVehicles = ({ product, history, session, fields }: Props) => {
    const { hideLogo } = fields;
    const dispatch = useDispatch();
    const [compatibleVehiclesList, setCompatibleVehiclesList] = useState<any>({});
    const ProductDetailsSectionTitle = getWidgetComponent("ProductDetails/ProductDetailsSectionTitle");
    const productListYmmdPage = useProductListYmmdPage();
    const [canSeeCompatibleVehicles, setCanSeeCompatibleVehicles] = useState<boolean>(false);
    const state: ApplicationState = useSelector(getState);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;

    if (!product || !isAuthenticated) {
        return null;
    }

    let mounted = true;

    useEffect(() => {
        const policy = getPolicy(state, "Products.CanSeeCompatibleVehicles");
        setCanSeeCompatibleVehicles(policy);

        dispatch(
            getCompatibleVehiclesList({
                productId: product.id,
                onComplete(resultProps) {
                    if (mounted) {
                        setCompatibleVehiclesList(resultProps?.apiResult?.result);
                    }
                },
            }),
        );

        return () => {
            mounted = false;
        };
    }, []);

    const goToYmmdResults = (year: string, make: string, model: string, diameter: string) => {
        const ymmdUrl = `${productListYmmdPage}?year=${year}&make=${make}&model=${model}&diameter=${diameter}`;
        dispatch(getProductsYMMD({ year, make, model, diameter, key: "diameter" }));
        history.push(ymmdUrl);
    };

    const vehiculeResults = compatibleVehiclesList?.makeResults?.length;

    if (typeof vehiculeResults === "undefined" || vehiculeResults === 0 || !canSeeCompatibleVehicles) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductDetailsSectionTitle
                sectionTitleValue={translate("Compatible Vehicles")}
                hideLogo={hideLogo}
            />
            <Accordion {...styles.accordion} headingLevel={2}>
                {compatibleVehiclesList.makeResults.map((make: MakesResultModel) => {
                    return (
                        <AccordionSection key={make.name} title={make.name} {...styles.accordionSection}>
                            {make.modelYearResults.map((modelYear: any) => {
                                const label = `${make.name} ${modelYear.model} ${modelYear.year}`;
                                const key = `${make.name}-${modelYear.model}-${modelYear.year}-${Math.floor(
                                    Math.random() * 10000,
                                )}`;

                                return (
                                    <Link
                                        key={key}
                                        onClick={() =>
                                            goToYmmdResults(
                                                modelYear.year,
                                                make.name,
                                                modelYear.model,
                                                compatibleVehiclesList.diameter,
                                            )
                                        }
                                        {...styles.link}
                                    >
                                        {label}
                                    </Link>
                                );
                            })}
                        </AccordionSection>
                    );
                })}
            </Accordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(withProduct(connect(mapStateToProps)(ProductDetailsCompatibleVehicles))),
    definition: {
        displayName: "Compatible Vehicles List",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.hideLogo,
                displayName: "Hide Logo in Section Title",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
